import { inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import type { Action, ActionCreator, Creator } from '@ngrx/store';
import { Store } from '@ngrx/store';
import { defer, filter, map, Observable, of, shareReplay, switchMap, take } from 'rxjs';

import type { ActionsFromConfig, ConfigObject } from './types';

type TypedAction = ActionCreator<any, Creator<any, { type: string }>>;
type ActionResponseResult<
  Success,
  Failed,
  SuccessAction extends Action,
  FailedAction extends Action,
> =
  | {
      isSuccess: true;
      action: SuccessAction;
      success: Success;
      failed: null;
    }
  | {
      isSuccess: false;
      action: FailedAction;
      success: null;
      failed: Failed;
    };

type NotFunction<T> = T extends Function ? never : T;

type SelectorFn<A extends Action> = (args: { store: Store; action: A }) => Observable<any>;
type UnwrapSelectorFn<A extends SelectorFn<any>> = A extends (...args: any) => Observable<infer R>
  ? R
  : never;

type SelectorFnResult<T> = [T] extends [SelectorFn<any>] ? UnwrapSelectorFn<T> : null;

/**
 * @description Create a stream that will dispatch provided action, listen to success and failed actions, and return the action found as well as provided selector for success and failed.
 * @remarks internal implementation for {@link createActionResponseFactory}. Can be used directly, but it's not recommended
 * @param actions$ - actions stream that will be listened to
 * @param store - store. Required to dispatch action
 * @param dispatch - action to dispatch. Note it will only dispatch after subscribing to the stream
 * @param opts.success - action that means the operation was successful
 * @param opts.failed - action that means the operation failed
 * @param opts.successResponse - function that'll be used to map the result action, if it is a success
 * @param opts.failedResponse - function that'll be used to map the result action, if it is a fail
 * @param opts.successIdentityCheck - if there are any other conditions to connect dispatched action and success action, add your filter function here. It will default to the check of payload.correlationId.
 * @param opts.successIdentityCheck - if there are any other conditions to connect dispatched action and failed action, add your filter function here. It will default to the check of payload.correlationId.
 * @param opts.identityCheck - if there are any other conditions to connect dispatched action and success or failed action, add your filter function here. Specific functions will take precedence over this option. It will default to the check of payload.correlationId.
 * @returns Object with: "action" - which is the failed or success action,
 * success field - which will be the result of provided successResponse, if the result was success, null otherwise
 * failed field - which will be the result of provided failedResponse, if the result was failed, null otherwise
 */
// eslint-disable-next-line max-lines-per-function
export function actionResponseFactory<
  Dispatch extends Action,
  Success extends TypedAction,
  Failed extends TypedAction,
  SuccessResponse extends SelectorFn<ReturnType<Success>> | undefined,
  FailedResponse extends SelectorFn<ReturnType<Failed>> | undefined,
>(
  actions$: Actions,
  store: Store,
  dispatch: NotFunction<Dispatch>,
  opts: {
    success: Success;
    failed: Failed;
    successResponse?: SuccessResponse;
    failedResponse?: FailedResponse;
    successIdentityCheck?: (payload: ReturnType<Success>) => boolean;
    failedIdentityCheck?: (payload: ReturnType<Failed>) => boolean;
    identityCheck?: (payload: ReturnType<Success | Failed>) => boolean;
  },
): Observable<
  ActionResponseResult<
    SelectorFnResult<SuccessResponse>,
    SelectorFnResult<FailedResponse>,
    ReturnType<Success>,
    ReturnType<Failed>
  >
> {
  const {
    success,
    failed,
    successIdentityCheck,
    failedIdentityCheck,
    identityCheck,
    successResponse,
    failedResponse,
  } = opts;

  return defer(() => {
    const response$ = actions$.pipe(
      ofType(success, failed),
      filter(action => {
        if (action.type === success.type && successIdentityCheck) {
          return successIdentityCheck(<ReturnType<Success>>action);
        } else if (action.type === failed.type && failedIdentityCheck) {
          return failedIdentityCheck(<ReturnType<Failed>>action);
        }
        if (identityCheck) {
          return identityCheck(action);
        }
        if ('payload' in dispatch && 'correlationId' in <any>dispatch.payload) {
          return (<any>dispatch.payload).correlationId === (<any>action)?.payload?.correlationId;
        }
        return true;
      }),
      take(1),
    );
    store.dispatch(<Action>dispatch);
    return response$;
  }).pipe(
    switchMap(action => {
      if (action.type === success.type) {
        const successResponse$ =
          successResponse?.({ store, action: <ReturnType<Success>>action }) ?? of(null);
        return successResponse$.pipe(
          map(success => ({
            action: <ReturnType<Success>>action,
            success,
            failed: null,
            isSuccess: <const>true,
          })),
        );
      }
      const failedResponse$ =
        failedResponse?.({ store, action: <ReturnType<Failed>>action }) ?? of(null);
      return failedResponse$.pipe(
        map(failed => ({
          action: <ReturnType<Failed>>action,
          success: null,
          failed,
          isSuccess: <const>false,
        })),
      );
    }),
    take(1),
  );
}

type UnwrapResponse<T> = T extends (...args: any) => infer R ? R : never;

// This will return a string with an error message if either failed or success is not defined
type VerifyActions<
  Input extends TypedAction,
  EntityActions extends ActionsFromConfig<ConfigObject<any>>,
  Success extends TypedAction | undefined,
  Failed extends TypedAction | undefined,
  SuccessErrorMessage = 'Success action not provided and not found in recognized actions. Add property success with action that indicates successful resolution',
  FailedErrorMessage = 'Failed action not provided and not found in recognized actions. Add property failed with action that indicates failed resolution',
> = [Success] extends [TypedAction]
  ? [Failed] extends [TypedAction]
    ? Input
    : EntityActions extends { [key in `${Input['type']}Failed`]: TypedAction }
      ? Input
      : FailedErrorMessage
  : EntityActions extends { [key in `${Input['type']}Success`]: TypedAction }
    ? EntityActions extends { [key in `${Input['type']}Failed`]: TypedAction }
      ? Input
      : FailedErrorMessage
    : SuccessErrorMessage;

function createActionResponseFactoryFunctionWrapper<
  EntityActions extends ActionsFromConfig<ConfigObject<any>>,
>(store: Store, actions$: Actions, recognizedActions?: EntityActions) {
  /**
   * @description Create a stream that will dispatch provided action, listen to success and failed actions, and return the action found as well as provided selector for success and failed.
   * @param action - action to dispatch. This should be an action creator and not called action. Note it will only dispatch after subscribing to the stream
   * @param payload - Parameters required by the action
   * @param opts.success - action that means the operation was successful. It will default to action from recognizedActions provided to {@link createActionResponseFactory}
   * @param opts.failed - action that means the operation failed. It will default to action from recognizedActions provided to {@link createActionResponseFactory}
   * @param opts.successResponse - function that'll be used to map the result action, if it is a success
   * @param opts.failedResponse - function that'll be used to map the result action, if it is a fail
   * @param opts.successIdentityCheck - if there are any other conditions to connect dispatched action and success action, add your filter function here. It will default to the check of payload.correlationId.
   * @param opts.successIdentityCheck - if there are any other conditions to connect dispatched action and failed action, add your filter function here. It will default to the check of payload.correlationId.
   * @param opts.identityCheck - if there are any other conditions to connect dispatched action and success or failed action, add your filter function here. Specific functions will take precedence over this option. It will default to the check of payload.correlationId.
   * @returns Object with: "action" - which is the failed or success action,
   * success field - which will be the result of provided successResponse, if the result was success, null otherwise
   * failed field - which will be the result of provided failedResponse, if the result was failed, null otherwise
   */
  function createActionResponseFactoryFunction<
    TAction extends TypedAction & { actionName?: string },
    Success extends TypedAction | undefined,
    Failed extends TypedAction | undefined,
    SuccessResponse extends SelectorFn<UnwrapResponse<SuccessAction>> | undefined,
    FailedResponse extends SelectorFn<UnwrapResponse<FailedAction>> | undefined,
    SuccessAction extends TypedAction = [Success] extends [TypedAction]
      ? Success
      : EntityActions[`${TAction['actionName']}Success`],
    FailedAction extends TypedAction = [Failed] extends [TypedAction]
      ? Failed
      : EntityActions[`${TAction['actionName']}Failed`],
  >(
    // Here error will be raied if either success or failed is not defined
    // Error will occur, because error string will not have overlap with provided action
    action: TAction & VerifyActions<TAction, EntityActions, Success, Failed>,
    payload: Parameters<TAction>[0],
    opts?: {
      success?: Success;
      failed?: Failed;
      successResponse?: SuccessResponse;
      failedResponse?: FailedResponse;
      successIdentityCheck?: (payload: UnwrapResponse<SuccessAction>) => boolean;
      failedIdentityCheck?: (payload: UnwrapResponse<FailedAction>) => boolean;
      identityCheck?: (payload: UnwrapResponse<SuccessAction | FailedAction>) => boolean;
    },
  ): Observable<
    ActionResponseResult<
      SelectorFnResult<SuccessResponse>,
      SelectorFnResult<FailedResponse>,
      ReturnType<SuccessAction>,
      ReturnType<FailedAction>
    >
  > {
    const {
      success,
      failed,
      successResponse,
      failedResponse,
      successIdentityCheck,
      failedIdentityCheck,
      identityCheck,
    } = opts || {};
    const successAction = success ?? recognizedActions?.[`${action.actionName}Success`];
    const failedAction = failed ?? recognizedActions?.[`${action.actionName}Failed`];

    return actionResponseFactory(actions$, store, action(payload), {
      success: <any>successAction,
      failed: <any>failedAction,
      successResponse,
      failedResponse,
      successIdentityCheck,
      failedIdentityCheck,
      identityCheck,
    });
  }

  return createActionResponseFactoryFunction;
}

/**

 * @example
 * basic usage
 * ```
 * class FacadeService {
 *   private factory = createActionResponseFactory(CandidateActions)
 *
 *   public fetchCandidates(): Observable<boolean> {
 *     return this.factor(CandidateActions.fetchCandidates, {paramsForAction}).pipe(
 *       map(result => result.action === CandidateActions.fetchCandidatesSuccess)
 *     )
 *   }
 * }
 * ```
 * @example
 * more advanced usage.
 * ```
 * class FacadeService {
 *   private factory = createActionResponseFactory(CandidateActions)
 *
 *   public fetchCandidates(): Observable<Candidates[]> {
 *     return this.factor(CandidateActions.fetchCandidates, {paramsForAction},
 *       successResponse: ({store}) => store.select(selectCandidates)
 *     ).pipe(
 *       map(({success}) => success ?? [])
 *     )
 *   }
 * }
 * ```
 * @param recognizedActions - dictionary of actions that follow the naming convention: {ActionName}, {ActionName}Success, {ActionName}Failed
 * @returns Factory function to create action-response streams {@link createActionResponseFactoryFunctionWrapper}
 */
export function createActionResponseFactory<
  EntityActions extends ActionsFromConfig<ConfigObject<any>>,
>(recognizedActions?: EntityActions) {
  return createActionResponseFactoryFunctionWrapper(
    inject(Store),
    inject(Actions),
    recognizedActions,
  );
}

/** @nuggets
 *
 * Create multicasted action stream for a data facade.
 *
 * **Usage:**
 * ```
 *   public updateUserSuccess$ = createActionStream(
 *     TellentCurrentUserEntityActions.updateCurrentUserDataSuccess,
 *   );
 * ```
 *
 */
export function createActionStream<T extends Array<ActionCreator<string, (...args: any[]) => any>>>(
  ...actionCreators: T
): Observable<ReturnType<T[number]>> {
  return inject(Actions).pipe(
    ofType(...actionCreators),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
}
